/**
 * Gmail integration API – per-module Gmail connection, status, configure, sync.
 * Base path: /gmail (axios baseURL already includes /apis/v1)
 * All calls use moduleId from the Edit Module route.
 */
import instance from './axios';

const GMAIL_BASE = '/gmail';

/**
 * Get Google OAuth URL for connecting Gmail to a module.
 * Frontend should open the returned authUrl in the same tab or popup.
 * @param {string} moduleId - Module ID from edit-module route
 * @returns {Promise<{ success: boolean, data?: { authUrl: string, message?: string }, message?: string }>}
 */
export const getGmailAuthUrl = async (moduleId) => {
    try {
        const { data } = await instance.get(`${GMAIL_BASE}/modules/${moduleId}/auth-url`);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to get Gmail auth URL';
        return { success: false, message };
    }
};

/**
 * Get connection state only (Option A).
 * Response: { connected: boolean, email: string|null, message?: string }
 * @param {string} moduleId
 * @returns {Promise<{ success: boolean, data?: { connected: boolean, email: string|null, message?: string }, message?: string }>}
 */
export const getGmailConnectionStatus = async (moduleId) => {
    try {
        const { data } = await instance.get(`${GMAIL_BASE}/modules/${moduleId}/connection-status`);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to get Gmail connection status';
        return { success: false, message, data: null };
    }
};

/**
 * Get full Gmail status for a module (Option B: gmailConnected, connectedEmail, gmailIntegration, processedEmailsCount, etc.).
 * Frontend should normalize: connected = data.connected ?? data.gmailConnected, email = data.email ?? data.connectedEmail
 * @param {string} moduleId
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const getGmailStatus = async (moduleId) => {
    try {
        const { data } = await instance.get(`${GMAIL_BASE}/modules/${moduleId}/status`);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to get Gmail status';
        return { success: false, message, data: null };
    }
};

/**
 * Disconnect Gmail from a module. DELETE /gmail/modules/:moduleId/disconnect
 * @param {string} moduleId
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const disconnectGmailForModule = async (moduleId) => {
    try {
        const { data } = await instance.delete(`${GMAIL_BASE}/modules/${moduleId}/disconnect`);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to disconnect Gmail';
        return { success: false, message };
    }
};

/**
 * Configure Gmail for a module (enabled, syncFrequency).
 * @param {string} moduleId
 * @param {object} body - { enabled: boolean, syncFrequency?: 'hourly' | 'daily' | 'manual' }
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const configureGmailForModule = async (moduleId, body) => {
    try {
        const { data } = await instance.post(`${GMAIL_BASE}/modules/${moduleId}/configure`, body);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to configure Gmail';
        return { success: false, message };
    }
};

/**
 * Trigger manual sync for a module.
 * @param {string} moduleId
 * @param {object} [body] - optional { maxResults?, query? }
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export const syncGmailForModule = async (moduleId, body = {}) => {
    try {
        const { data } = await instance.post(`${GMAIL_BASE}/modules/${moduleId}/sync`, body);
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Sync failed';
        return { success: false, message };
    }
};

/**
 * Get processed emails for a module (paginated).
 * @param {string} moduleId
 * @param {object} params - { page?: number, limit?: number }
 * @returns {Promise<{ success: boolean, data?: { results?: array, total?: number }, message?: string }>}
 */
export const getGmailProcessedEmails = async (moduleId, params = {}) => {
    const { page = 1, limit = 10 } = params;
    try {
        const { data } = await instance.get(`${GMAIL_BASE}/modules/${moduleId}/processed-emails`, {
            params: { page, limit },
        });
        return { success: true, data: data?.data ?? data };
    } catch (e) {
        const message = e.response?.data?.message ?? e.message ?? 'Failed to fetch processed emails';
        return { success: false, message, data: null };
    }
};
